import {
  DEFAULT_MEMBER_GROUPS,
  normalizeEmail,
  normalizeRequiredText,
  type Choir,
  type InvitationCode,
  type JoinRequest,
  type MemberGroup,
  type MemberGroupTag,
  type MemberManagementData,
  type Membership,
  type PermissionGroup,
  type PermissionGroupMember,
  type PermissionGroupPermission,
  type Season,
  type SeasonMembership,
  type UserAccount
} from './model';
import {
  buildPermissionGroup,
  buildInitialChoirSetup,
  buildInvitationCode,
  buildPendingJoinRequest,
  buildReviewedJoinRequest,
  canCreatorAddFirstMemberToGroup,
  canManageMemberGroupByRoleOrGrant,
  getInvitationCodeUsabilityProblem,
  hasNormalizedNameCollision,
  isPendingJoinRequestForChoirSeason,
  nextDisplayOrder,
  resolveEffectivePermissionKeys,
  shouldGrantFirstMemberGroupManager
} from './memberManagementRules';

type EntityPrefix =
  | 'user'
  | 'choir'
  | 'season'
  | 'membership'
  | 'seasonMembership'
  | 'memberGroup'
  | 'memberGroupTag'
  | 'permissionGroup'
  | 'invitationCode'
  | 'joinRequest'
  | 'schedule'
  | 'attendanceCategory'
  | 'attendanceEvent'
  | 'attendanceParticipantSnapshot'
  | 'attendanceRecord'
  | 'assignment'
  | 'assignmentDeliveryRule'
  | 'assignmentSubmission'
  | 'fileUpload'
  | 'objectCleanupFailure'
  | 'song'
  | 'resourceFolder'
  | 'resourceFile'
  | 'notification';

export interface DomainPorts {
  readonly now: () => string;
  readonly newId: (prefix: EntityPrefix) => string;
}

export function createUserAccount(
  data: MemberManagementData,
  input: {
    readonly email: string;
    readonly userId: string;
    readonly createdAt: string;
  }
): { readonly data: MemberManagementData; readonly user: UserAccount } {
  const email = normalizeEmail(input.email);

  if (data.users.some((user) => user.email === email)) {
    throw new Error('Email is already registered');
  }

  const user: UserAccount = {
    userId: input.userId,
    email,
    createdAt: input.createdAt
  };

  return {
    data: {
      ...data,
      users: [...data.users, user]
    },
    user
  };
}

export function createChoir(
  data: MemberManagementData,
  input: {
    readonly userId: string;
    readonly name: string;
    readonly ports: DomainPorts;
  }
): {
  readonly data: MemberManagementData;
  readonly choir: Choir;
  readonly season: Season;
  readonly membership: Membership;
  readonly seasonMembership: SeasonMembership;
  readonly memberGroups: readonly MemberGroup[];
} {
  const user = findUser(data, input.userId);
  const choirId = input.ports.newId('choir');
  const seasonId = input.ports.newId('season');
  const membershipId = input.ports.newId('membership');
  const seasonMembershipId = input.ports.newId('seasonMembership');
  const createdAt = input.ports.now();
  const {
    choir,
    currentSeason: season,
    currentMembership: membership,
    currentSeasonMembership: seasonMembership,
    memberGroups
  } = buildInitialChoirSetup({
    user,
    choirName: input.name,
    ids: {
      choirId,
      seasonId,
      membershipId,
      seasonMembershipId,
      memberGroupIds: DEFAULT_MEMBER_GROUPS.map(() => input.ports.newId('memberGroup'))
    },
    createdAt
  });

  return {
    data: {
      ...data,
      choirs: [...data.choirs, choir],
      seasons: [...data.seasons, season],
      memberships: [...data.memberships, membership],
      seasonMemberships: [...data.seasonMemberships, seasonMembership],
      memberGroups: [...data.memberGroups, ...memberGroups]
    },
    choir,
    season,
    membership,
    seasonMembership,
    memberGroups
  };
}

export function createMemberGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly name: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly memberGroup: MemberGroup } {
  const actor = findMembership(data, input.actorMembershipId);
  const season = findCurrentSeason(data, actor.choirId);
  const actorSeasonMembership = findSeasonMembershipByMembershipId(
    data,
    actor.membershipId,
    actor.choirId,
    season.seasonId
  );
  const name = normalizeRequiredText(input.name, 'Member group name');

  const seasonMemberGroups = data.memberGroups.filter((group) => group.seasonId === season.seasonId);

  if (hasNormalizedNameCollision(seasonMemberGroups, name, 'memberGroupId')) {
    throw new Error('Member group name already exists in this season');
  }

  const displayOrder = nextDisplayOrder(seasonMemberGroups);
  const memberGroup: MemberGroup = {
    memberGroupId: input.ports.newId('memberGroup'),
    choirId: actor.choirId,
    seasonId: season.seasonId,
    name,
    isVocalPart: false,
    isDefault: false,
    displayOrder,
    createdBySeasonMembershipId: actorSeasonMembership.seasonMembershipId,
    createdAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      memberGroups: [...data.memberGroups, memberGroup]
    },
    memberGroup
  };
}

export function renameMemberGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
    readonly name: string;
  }
): { readonly data: MemberManagementData; readonly memberGroup: MemberGroup } {
  const actor = findMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertCanManageMemberGroup(data, actor, memberGroup);
  const name = normalizeRequiredText(input.name, 'Member group name');

  if (
    hasNormalizedNameCollision(
      data.memberGroups.filter((group) => group.seasonId === memberGroup.seasonId),
      name,
      'memberGroupId',
      memberGroup.memberGroupId
    )
  ) {
    throw new Error('Member group name already exists in this season');
  }

  const renamedMemberGroup: MemberGroup = {
    ...memberGroup,
    name
  };

  return {
    data: {
      ...data,
      memberGroups: replaceById(
        data.memberGroups,
        (candidate) => candidate.memberGroupId === renamedMemberGroup.memberGroupId,
        renamedMemberGroup
      )
    },
    memberGroup: renamedMemberGroup
  };
}

export function deleteMemberGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
  }
): { readonly data: MemberManagementData } {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertMembershipBelongsToMemberGroupChoir(actor, memberGroup);

  if (memberGroup.isDefault) {
    throw new Error('Default member groups cannot be deleted');
  }

  if (data.seasonMembershipMemberGroups.some((link) => link.memberGroupId === memberGroup.memberGroupId)) {
    throw new Error('Member group must be empty before deletion');
  }

  const deletedTagIds = new Set(
    data.memberGroupTags
      .filter((tag) => tag.memberGroupId === memberGroup.memberGroupId)
      .map((tag) => tag.memberGroupTagId)
  );

  return {
    data: {
      ...data,
      memberGroups: data.memberGroups.filter((candidate) => candidate.memberGroupId !== memberGroup.memberGroupId),
      seasonMembershipMemberGroups: data.seasonMembershipMemberGroups.filter(
        (link) => link.memberGroupId !== memberGroup.memberGroupId
      ),
      memberGroupManagerGrants: data.memberGroupManagerGrants.filter(
        (grant) => grant.memberGroupId !== memberGroup.memberGroupId
      ),
      memberGroupTags: data.memberGroupTags.filter((tag) => tag.memberGroupId !== memberGroup.memberGroupId),
      seasonMembershipMemberGroupTags: data.seasonMembershipMemberGroupTags.filter(
        (link) => !deletedTagIds.has(link.memberGroupTagId)
      )
    }
  };
}

export function addSeasonMembershipToMemberGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData } {
  const actor = findMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertCanAddMemberToMemberGroup(data, actor, memberGroup);
  const targetSeasonMembership = findSeasonMembership(data, input.seasonMembershipId);

  if (
    targetSeasonMembership.choirId !== memberGroup.choirId ||
    targetSeasonMembership.seasonId !== memberGroup.seasonId
  ) {
    throw new Error('Season membership must belong to the member group season');
  }

  const targetMembership = findMembership(data, targetSeasonMembership.membershipId);

  if (isSeasonMembershipInMemberGroup(data, input.seasonMembershipId, input.memberGroupId)) {
    throw new Error('Season membership is already in member group');
  }

  const wasEmpty = !data.seasonMembershipMemberGroups.some((link) => link.memberGroupId === input.memberGroupId);
  const nextLinks = [
    ...data.seasonMembershipMemberGroups,
    {
      seasonMembershipId: input.seasonMembershipId,
      memberGroupId: input.memberGroupId
    }
  ];
  const shouldGrantFirstManager = shouldGrantFirstMemberGroupManager({
    wasEmpty,
    targetMemberRole: targetMembership.role,
    alreadyHasGrant: hasMemberGroupManagerGrant(data, input.seasonMembershipId, input.memberGroupId)
  });

  return {
    data: {
      ...data,
      seasonMembershipMemberGroups: nextLinks,
      memberGroupManagerGrants: shouldGrantFirstManager
        ? [
            ...data.memberGroupManagerGrants,
            {
              memberGroupId: input.memberGroupId,
              seasonMembershipId: input.seasonMembershipId,
              grantedAt: input.ports.now()
            }
          ]
        : data.memberGroupManagerGrants
    }
  };
}

export function removeSeasonMembershipFromMemberGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
  }
): { readonly data: MemberManagementData } {
  const actor = findMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertCanManageMemberGroup(data, actor, memberGroup);

  if (!isSeasonMembershipInMemberGroup(data, input.seasonMembershipId, input.memberGroupId)) {
    throw new Error('Season membership is not in member group');
  }

  const tagIdsInGroup = new Set(
    data.memberGroupTags.filter((tag) => tag.memberGroupId === input.memberGroupId).map((tag) => tag.memberGroupTagId)
  );

  return {
    data: {
      ...data,
      seasonMembershipMemberGroups: data.seasonMembershipMemberGroups.filter(
        (link) => !(link.memberGroupId === input.memberGroupId && link.seasonMembershipId === input.seasonMembershipId)
      ),
      memberGroupManagerGrants: data.memberGroupManagerGrants.filter(
        (grant) =>
          !(grant.memberGroupId === input.memberGroupId && grant.seasonMembershipId === input.seasonMembershipId)
      ),
      seasonMembershipMemberGroupTags: data.seasonMembershipMemberGroupTags.filter(
        (link) => !(link.seasonMembershipId === input.seasonMembershipId && tagIdsInGroup.has(link.memberGroupTagId))
      )
    }
  };
}

export function setMemberGroupManagerGrant(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
    readonly seasonMembershipId: string;
    readonly enabled: boolean;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData } {
  const actor = findMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertCanManageMemberGroup(data, actor, memberGroup);
  const targetSeasonMembership = findSeasonMembership(data, input.seasonMembershipId);

  if (
    targetSeasonMembership.choirId !== memberGroup.choirId ||
    targetSeasonMembership.seasonId !== memberGroup.seasonId
  ) {
    throw new Error('Season membership must belong to the member group season');
  }

  const targetMembership = findMembership(data, targetSeasonMembership.membershipId);
  if (targetMembership.role === 'admin') {
    throw new Error('Admin member group management is implicit');
  }

  if (!isSeasonMembershipInMemberGroup(data, input.seasonMembershipId, input.memberGroupId)) {
    throw new Error('Group manager must belong to member group');
  }

  const existingGrant = hasMemberGroupManagerGrant(data, input.seasonMembershipId, input.memberGroupId);
  if (input.enabled && existingGrant) {
    return { data };
  }

  if (input.enabled) {
    return {
      data: {
        ...data,
        memberGroupManagerGrants: [
          ...data.memberGroupManagerGrants,
          {
            memberGroupId: input.memberGroupId,
            seasonMembershipId: input.seasonMembershipId,
            grantedAt: input.ports.now()
          }
        ]
      }
    };
  }

  return {
    data: {
      ...data,
      memberGroupManagerGrants: data.memberGroupManagerGrants.filter(
        (grant) =>
          !(grant.memberGroupId === input.memberGroupId && grant.seasonMembershipId === input.seasonMembershipId)
      )
    }
  };
}

export function createMemberGroupTag(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupId: string;
    readonly name: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly memberGroupTag: MemberGroupTag } {
  const actor = findMembership(data, input.actorMembershipId);
  const memberGroup = findMemberGroup(data, input.memberGroupId);
  assertCanManageMemberGroup(data, actor, memberGroup);
  const name = normalizeRequiredText(input.name, 'Member group tag name');

  const groupTags = data.memberGroupTags.filter((tag) => tag.memberGroupId === input.memberGroupId);

  if (hasNormalizedNameCollision(groupTags, name, 'memberGroupTagId')) {
    throw new Error('Member group tag name already exists in this group');
  }

  const displayOrder = nextDisplayOrder(groupTags);
  const memberGroupTag: MemberGroupTag = {
    memberGroupTagId: input.ports.newId('memberGroupTag'),
    memberGroupId: input.memberGroupId,
    name,
    displayOrder,
    createdAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      memberGroupTags: [...data.memberGroupTags, memberGroupTag]
    },
    memberGroupTag
  };
}

export function renameMemberGroupTag(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupTagId: string;
    readonly name: string;
  }
): { readonly data: MemberManagementData; readonly memberGroupTag: MemberGroupTag } {
  const tag = findMemberGroupTag(data, input.memberGroupTagId);
  const memberGroup = findMemberGroup(data, tag.memberGroupId);
  const actor = findMembership(data, input.actorMembershipId);
  assertCanManageMemberGroup(data, actor, memberGroup);
  const name = normalizeRequiredText(input.name, 'Member group tag name');

  if (
    hasNormalizedNameCollision(
      data.memberGroupTags.filter((candidate) => candidate.memberGroupId === memberGroup.memberGroupId),
      name,
      'memberGroupTagId',
      tag.memberGroupTagId
    )
  ) {
    throw new Error('Member group tag name already exists in this group');
  }

  const renamedTag: MemberGroupTag = {
    ...tag,
    name
  };

  return {
    data: {
      ...data,
      memberGroupTags: replaceById(
        data.memberGroupTags,
        (candidate) => candidate.memberGroupTagId === renamedTag.memberGroupTagId,
        renamedTag
      )
    },
    memberGroupTag: renamedTag
  };
}

export function deleteMemberGroupTag(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupTagId: string;
  }
): { readonly data: MemberManagementData } {
  const tag = findMemberGroupTag(data, input.memberGroupTagId);
  const memberGroup = findMemberGroup(data, tag.memberGroupId);
  const actor = findMembership(data, input.actorMembershipId);
  assertCanManageMemberGroup(data, actor, memberGroup);

  return {
    data: {
      ...data,
      memberGroupTags: data.memberGroupTags.filter(
        (candidate) => candidate.memberGroupTagId !== input.memberGroupTagId
      ),
      seasonMembershipMemberGroupTags: data.seasonMembershipMemberGroupTags.filter(
        (link) => link.memberGroupTagId !== input.memberGroupTagId
      )
    }
  };
}

export function setMemberGroupTagMembership(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly memberGroupTagId: string;
    readonly seasonMembershipId: string;
    readonly enabled: boolean;
  }
): { readonly data: MemberManagementData } {
  const tag = findMemberGroupTag(data, input.memberGroupTagId);
  const memberGroup = findMemberGroup(data, tag.memberGroupId);
  const actor = findMembership(data, input.actorMembershipId);
  assertCanManageMemberGroup(data, actor, memberGroup);

  if (!isSeasonMembershipInMemberGroup(data, input.seasonMembershipId, memberGroup.memberGroupId)) {
    throw new Error('Tagged member must belong to member group');
  }

  const existingLink = data.seasonMembershipMemberGroupTags.some(
    (link) => link.memberGroupTagId === input.memberGroupTagId && link.seasonMembershipId === input.seasonMembershipId
  );

  if (input.enabled && existingLink) {
    return { data };
  }

  if (input.enabled) {
    return {
      data: {
        ...data,
        seasonMembershipMemberGroupTags: [
          ...data.seasonMembershipMemberGroupTags,
          {
            memberGroupTagId: input.memberGroupTagId,
            seasonMembershipId: input.seasonMembershipId
          }
        ]
      }
    };
  }

  return {
    data: {
      ...data,
      seasonMembershipMemberGroupTags: data.seasonMembershipMemberGroupTags.filter(
        (link) =>
          !(link.memberGroupTagId === input.memberGroupTagId && link.seasonMembershipId === input.seasonMembershipId)
      )
    }
  };
}

export function createPermissionGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly name: string;
    readonly permissionKeys: readonly string[];
    readonly ports: DomainPorts;
  }
): {
  readonly data: MemberManagementData;
  readonly permissionGroup: PermissionGroup;
  readonly permissions: readonly PermissionGroupPermission[];
} {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const season = findCurrentSeason(data, actor.choirId);
  const now = input.ports.now();
  const { permissionGroup, permissions } = buildPermissionGroup({
    permissionGroupId: input.ports.newId('permissionGroup'),
    choirId: actor.choirId,
    seasonId: season.seasonId,
    name: input.name,
    permissionKeys: input.permissionKeys,
    createdAt: now,
    updatedAt: now
  });

  if (hasPermissionGroupNameCollision(data, permissionGroup)) {
    throw new Error('Permission group name already exists in this season');
  }

  return {
    data: {
      ...data,
      permissionGroups: [...data.permissionGroups, permissionGroup],
      permissionGroupPermissions: [...data.permissionGroupPermissions, ...permissions]
    },
    permissionGroup,
    permissions
  };
}

export function updatePermissionGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly permissionGroupId: string;
    readonly name: string;
    readonly permissionKeys: readonly string[];
    readonly ports: DomainPorts;
  }
): {
  readonly data: MemberManagementData;
  readonly permissionGroup: PermissionGroup;
  readonly permissions: readonly PermissionGroupPermission[];
} {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const existing = findPermissionGroup(data, input.permissionGroupId);
  assertPermissionGroupInActorCurrentSeason(data, actor, existing);
  const { permissionGroup, permissions } = buildPermissionGroup({
    permissionGroupId: existing.permissionGroupId,
    choirId: existing.choirId,
    seasonId: existing.seasonId,
    name: input.name,
    permissionKeys: input.permissionKeys,
    createdAt: existing.createdAt,
    updatedAt: input.ports.now()
  });

  if (hasPermissionGroupNameCollision(data, permissionGroup, existing.permissionGroupId)) {
    throw new Error('Permission group name already exists in this season');
  }

  return {
    data: {
      ...data,
      permissionGroups: replaceById(
        data.permissionGroups,
        (candidate) => candidate.permissionGroupId === permissionGroup.permissionGroupId,
        permissionGroup
      ),
      permissionGroupPermissions: [
        ...data.permissionGroupPermissions.filter(
          (permission) => permission.permissionGroupId !== permissionGroup.permissionGroupId
        ),
        ...permissions
      ]
    },
    permissionGroup,
    permissions
  };
}

export function deletePermissionGroup(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly permissionGroupId: string;
  }
): { readonly data: MemberManagementData } {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const permissionGroup = findPermissionGroup(data, input.permissionGroupId);
  assertPermissionGroupInActorCurrentSeason(data, actor, permissionGroup);

  return {
    data: {
      ...data,
      permissionGroups: data.permissionGroups.filter(
        (candidate) => candidate.permissionGroupId !== permissionGroup.permissionGroupId
      ),
      permissionGroupPermissions: data.permissionGroupPermissions.filter(
        (permission) => permission.permissionGroupId !== permissionGroup.permissionGroupId
      ),
      permissionGroupMembers: data.permissionGroupMembers.filter(
        (member) => member.permissionGroupId !== permissionGroup.permissionGroupId
      )
    }
  };
}

export function addPermissionGroupMember(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly permissionGroupId: string;
    readonly seasonMembershipId: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly permissionGroupMember: PermissionGroupMember } {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const permissionGroup = findPermissionGroup(data, input.permissionGroupId);
  assertPermissionGroupInActorCurrentSeason(data, actor, permissionGroup);
  const targetSeasonMembership = findSeasonMembership(data, input.seasonMembershipId);

  if (
    targetSeasonMembership.choirId !== permissionGroup.choirId ||
    targetSeasonMembership.seasonId !== permissionGroup.seasonId
  ) {
    throw new Error('Season membership must belong to the permission group season');
  }

  if (isSeasonMembershipInPermissionGroup(data, input.seasonMembershipId, input.permissionGroupId)) {
    throw new Error('Season membership is already in permission group');
  }

  const permissionGroupMember: PermissionGroupMember = {
    permissionGroupId: input.permissionGroupId,
    seasonMembershipId: input.seasonMembershipId,
    grantedAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      permissionGroupMembers: [...data.permissionGroupMembers, permissionGroupMember]
    },
    permissionGroupMember
  };
}

export function removePermissionGroupMember(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly permissionGroupId: string;
    readonly seasonMembershipId: string;
  }
): { readonly data: MemberManagementData } {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const permissionGroup = findPermissionGroup(data, input.permissionGroupId);
  assertPermissionGroupInActorCurrentSeason(data, actor, permissionGroup);

  if (!isSeasonMembershipInPermissionGroup(data, input.seasonMembershipId, input.permissionGroupId)) {
    throw new Error('Season membership is not in permission group');
  }

  return {
    data: {
      ...data,
      permissionGroupMembers: data.permissionGroupMembers.filter(
        (member) =>
          !(
            member.permissionGroupId === input.permissionGroupId &&
            member.seasonMembershipId === input.seasonMembershipId
          )
      )
    }
  };
}

export function generateInvitationCode(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly code: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly invitationCode: InvitationCode } {
  const actor = findMembership(data, input.actorMembershipId);
  const season = findCurrentSeason(data, actor.choirId);

  if (!hasSeasonMembershipByMembershipId(data, actor.membershipId, actor.choirId, season.seasonId)) {
    throw new Error('Current season membership is required');
  }

  const code = normalizeRequiredText(input.code, 'Invitation code');

  if (data.invitationCodes.some((invitationCode) => invitationCode.code === code)) {
    throw new Error('Invitation code already exists');
  }

  const createdAt = input.ports.now();
  const invitationCode = buildInvitationCode({
    invitationCodeId: input.ports.newId('invitationCode'),
    code,
    choirId: actor.choirId,
    seasonId: season.seasonId,
    createdByMembershipId: actor.membershipId,
    createdAt
  });

  return {
    data: {
      ...data,
      invitationCodes: [...data.invitationCodes, invitationCode]
    },
    invitationCode
  };
}

export function deleteInvitationCode(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly invitationCodeId: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly invitationCode: InvitationCode } {
  const actor = findAdminMembership(data, input.actorMembershipId);
  const invitationCode = findInvitationCodeById(data, input.invitationCodeId);

  if (invitationCode.choirId !== actor.choirId) {
    throw new Error('Invitation code belongs to a different choir');
  }

  const usabilityProblem = getInvitationCodeUsabilityProblem(invitationCode, input.ports.now());

  if (usabilityProblem === 'deleted') {
    throw new Error('Invitation code is not active');
  }

  if (usabilityProblem === 'expired') {
    throw new Error('Invitation code is expired');
  }

  const deletedInvitationCode: InvitationCode = {
    ...invitationCode,
    status: 'deleted',
    deletedAt: input.ports.now()
  };

  return {
    data: {
      ...data,
      invitationCodes: replaceById(
        data.invitationCodes,
        (candidate) => candidate.invitationCodeId === deletedInvitationCode.invitationCodeId,
        deletedInvitationCode
      )
    },
    invitationCode: deletedInvitationCode
  };
}

export function submitJoinRequest(
  data: MemberManagementData,
  input: {
    readonly userId: string;
    readonly code: string;
    readonly nickname: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly joinRequest: JoinRequest } {
  const user = findUser(data, input.userId);
  const invitationCode = findInvitationCodeByCode(data, input.code);
  const submittedAt = input.ports.now();

  const usabilityProblem = getInvitationCodeUsabilityProblem(invitationCode, submittedAt);

  if (usabilityProblem === 'deleted') {
    throw new Error('Invitation code is deleted');
  }

  if (usabilityProblem === 'expired') {
    throw new Error('Invitation code is expired');
  }

  if (hasSeasonMembership(data, user.userId, invitationCode.choirId, invitationCode.seasonId)) {
    throw new Error('User is already a current season member of this choir');
  }

  if (
    data.joinRequests.some((joinRequest) =>
      isPendingJoinRequestForChoirSeason(joinRequest, {
        userId: user.userId,
        choirId: invitationCode.choirId,
        seasonId: invitationCode.seasonId
      })
    )
  ) {
    throw new Error('A pending join request already exists for this choir current season');
  }

  const joinRequest = buildPendingJoinRequest({
    joinRequestId: input.ports.newId('joinRequest'),
    userId: user.userId,
    invitationCode,
    nickname: input.nickname,
    submittedAt
  });

  return {
    data: {
      ...data,
      joinRequests: [...data.joinRequests, joinRequest]
    },
    joinRequest
  };
}

export function approveJoinRequest(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly joinRequestId: string;
    readonly memberGroupIds: readonly string[];
    readonly ports: DomainPorts;
  }
): {
  readonly data: MemberManagementData;
  readonly joinRequest: JoinRequest;
  readonly membership: Membership;
  readonly seasonMembership: SeasonMembership;
} {
  const actor = findMembership(data, input.actorMembershipId);
  const joinRequest = findJoinRequest(data, input.joinRequestId);

  assertCanReview(data, actor, joinRequest);
  assertPending(joinRequest);
  assertUniqueMemberGroupIds(input.memberGroupIds);
  assertMemberGroupsBelongToSeason(data, input.memberGroupIds, joinRequest.seasonId);

  if (hasSeasonMembership(data, joinRequest.userId, joinRequest.choirId, joinRequest.seasonId)) {
    throw new Error('User is already a current season member of this choir');
  }

  const createdAt = input.ports.now();
  const existingMembership = data.memberships.find(
    (membership) => membership.userId === joinRequest.userId && membership.choirId === joinRequest.choirId
  );
  const membership: Membership =
    existingMembership ??
    ({
      membershipId: input.ports.newId('membership'),
      userId: joinRequest.userId,
      choirId: joinRequest.choirId,
      role: 'member',
      createdAt
    } satisfies Membership);
  const seasonMembership: SeasonMembership = {
    seasonMembershipId: input.ports.newId('seasonMembership'),
    membershipId: membership.membershipId,
    choirId: joinRequest.choirId,
    seasonId: joinRequest.seasonId,
    nickname: joinRequest.nickname,
    status: 'active',
    createdAt
  };
  const reviewedJoinRequest = buildReviewedJoinRequest({
    joinRequest,
    status: 'approved',
    reviewerMembershipId: actor.membershipId,
    reviewedAt: createdAt
  });
  const selectedGroupLinks = input.memberGroupIds.map((memberGroupId) => ({
    seasonMembershipId: seasonMembership.seasonMembershipId,
    memberGroupId
  }));

  return {
    data: {
      ...data,
      memberships: existingMembership ? data.memberships : [...data.memberships, membership],
      seasonMemberships: [...data.seasonMemberships, seasonMembership],
      seasonMembershipMemberGroups: [...data.seasonMembershipMemberGroups, ...selectedGroupLinks],
      joinRequests: replaceById(
        data.joinRequests,
        (candidate) => candidate.joinRequestId === reviewedJoinRequest.joinRequestId,
        reviewedJoinRequest
      )
    },
    joinRequest: reviewedJoinRequest,
    membership,
    seasonMembership
  };
}

export function rejectJoinRequest(
  data: MemberManagementData,
  input: {
    readonly actorMembershipId: string;
    readonly joinRequestId: string;
    readonly rejectionReason?: string;
    readonly ports: DomainPorts;
  }
): { readonly data: MemberManagementData; readonly joinRequest: JoinRequest } {
  const actor = findMembership(data, input.actorMembershipId);
  const joinRequest = findJoinRequest(data, input.joinRequestId);

  assertCanReview(data, actor, joinRequest);
  assertPending(joinRequest);

  const reviewedJoinRequest = buildReviewedJoinRequest({
    joinRequest,
    status: 'rejected',
    reviewerMembershipId: actor.membershipId,
    reviewedAt: input.ports.now(),
    rejectionReason: input.rejectionReason
  });

  return {
    data: {
      ...data,
      joinRequests: replaceById(
        data.joinRequests,
        (candidate) => candidate.joinRequestId === reviewedJoinRequest.joinRequestId,
        reviewedJoinRequest
      )
    },
    joinRequest: reviewedJoinRequest
  };
}

function findUser(data: MemberManagementData, userId: string): UserAccount {
  const user = data.users.find((candidate) => candidate.userId === userId);

  if (!user) {
    throw new Error('User not found');
  }

  return user;
}

function findMembership(data: MemberManagementData, membershipId: string): Membership {
  const membership = data.memberships.find((candidate) => candidate.membershipId === membershipId);

  if (!membership) {
    throw new Error('Membership not found');
  }

  return membership;
}

function findSeasonMembership(data: MemberManagementData, seasonMembershipId: string): SeasonMembership {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) => candidate.seasonMembershipId === seasonMembershipId
  );

  if (!seasonMembership) {
    throw new Error('Season membership not found');
  }

  return seasonMembership;
}

function findAdminMembership(data: MemberManagementData, membershipId: string): Membership {
  const membership = findMembership(data, membershipId);

  if (membership.role !== 'admin') {
    throw new Error('Only admins can perform this operation');
  }

  return membership;
}

function findCurrentSeason(data: MemberManagementData, choirId: string): Season {
  const currentSeasons = data.seasons.filter((candidate) => candidate.choirId === choirId && candidate.isCurrent);

  if (currentSeasons.length !== 1) {
    throw new Error('Choir must have exactly one current season');
  }

  return currentSeasons[0];
}

function findSeasonMembershipByMembershipId(
  data: MemberManagementData,
  membershipId: string,
  choirId: string,
  seasonId: string
): SeasonMembership {
  const seasonMembership = data.seasonMemberships.find(
    (candidate) =>
      candidate.membershipId === membershipId &&
      candidate.choirId === choirId &&
      candidate.seasonId === seasonId &&
      candidate.status === 'active'
  );

  if (!seasonMembership) {
    throw new Error('Current season membership is required');
  }

  return seasonMembership;
}

function findMemberGroup(data: MemberManagementData, memberGroupId: string): MemberGroup {
  const memberGroup = data.memberGroups.find((candidate) => candidate.memberGroupId === memberGroupId);

  if (!memberGroup) {
    throw new Error('Member group not found');
  }

  return memberGroup;
}

function findMemberGroupTag(data: MemberManagementData, memberGroupTagId: string): MemberGroupTag {
  const tag = data.memberGroupTags.find((candidate) => candidate.memberGroupTagId === memberGroupTagId);

  if (!tag) {
    throw new Error('Member group tag not found');
  }

  return tag;
}

function isSeasonMembershipInMemberGroup(
  data: MemberManagementData,
  seasonMembershipId: string,
  memberGroupId: string
): boolean {
  return data.seasonMembershipMemberGroups.some(
    (link) => link.seasonMembershipId === seasonMembershipId && link.memberGroupId === memberGroupId
  );
}

function hasMemberGroupManagerGrant(
  data: MemberManagementData,
  seasonMembershipId: string,
  memberGroupId: string
): boolean {
  return data.memberGroupManagerGrants.some(
    (grant) => grant.seasonMembershipId === seasonMembershipId && grant.memberGroupId === memberGroupId
  );
}

function findPermissionGroup(data: MemberManagementData, permissionGroupId: string): PermissionGroup {
  const permissionGroup = data.permissionGroups.find((candidate) => candidate.permissionGroupId === permissionGroupId);

  if (!permissionGroup) {
    throw new Error('Permission group not found');
  }

  return permissionGroup;
}

function hasPermissionGroupNameCollision(
  data: MemberManagementData,
  permissionGroup: PermissionGroup,
  ignoredPermissionGroupId?: string
): boolean {
  return hasNormalizedNameCollision(
    data.permissionGroups.filter(
      (candidate) => candidate.choirId === permissionGroup.choirId && candidate.seasonId === permissionGroup.seasonId
    ),
    permissionGroup.name,
    'permissionGroupId',
    ignoredPermissionGroupId
  );
}

function assertPermissionGroupInActorCurrentSeason(
  data: MemberManagementData,
  actor: Membership,
  permissionGroup: PermissionGroup
): void {
  if (permissionGroup.choirId !== actor.choirId) {
    throw new Error('Permission group belongs to a different choir');
  }

  if (permissionGroup.seasonId !== findCurrentSeason(data, actor.choirId).seasonId) {
    throw new Error('Permission group is not in the current season');
  }
}

function isSeasonMembershipInPermissionGroup(
  data: MemberManagementData,
  seasonMembershipId: string,
  permissionGroupId: string
): boolean {
  return data.permissionGroupMembers.some(
    (member) => member.seasonMembershipId === seasonMembershipId && member.permissionGroupId === permissionGroupId
  );
}

function assertCanManageMemberGroup(data: MemberManagementData, actor: Membership, memberGroup: MemberGroup): void {
  assertMembershipBelongsToMemberGroupChoir(actor, memberGroup);

  const actorSeasonMembership =
    actor.role === 'admin' ? undefined : findActorSeasonMembershipForMemberGroup(data, actor, memberGroup);
  if (
    canManageMemberGroupByRoleOrGrant({
      memberRole: actor.role,
      currentSeasonMembershipId: actorSeasonMembership?.seasonMembershipId,
      memberGroupId: memberGroup.memberGroupId,
      managerGrants: data.memberGroupManagerGrants
    })
  ) {
    return;
  }

  throw new Error('Member group manager permission is required');
}

function assertCanAddMemberToMemberGroup(
  data: MemberManagementData,
  actor: Membership,
  memberGroup: MemberGroup
): void {
  assertMembershipBelongsToMemberGroupChoir(actor, memberGroup);

  const actorSeasonMembership =
    actor.role === 'admin' ? undefined : findActorSeasonMembershipForMemberGroup(data, actor, memberGroup);
  if (
    canManageMemberGroupByRoleOrGrant({
      memberRole: actor.role,
      currentSeasonMembershipId: actorSeasonMembership?.seasonMembershipId,
      memberGroupId: memberGroup.memberGroupId,
      managerGrants: data.memberGroupManagerGrants
    })
  ) {
    return;
  }

  if (
    canCreatorAddFirstMemberToGroup({
      memberCount: data.seasonMembershipMemberGroups.filter((link) => link.memberGroupId === memberGroup.memberGroupId)
        .length,
      createdBySeasonMembershipId: memberGroup.createdBySeasonMembershipId,
      currentSeasonMembershipId: actorSeasonMembership?.seasonMembershipId
    })
  ) {
    return;
  }

  throw new Error('Member group manager permission is required');
}

function findActorSeasonMembershipForMemberGroup(
  data: MemberManagementData,
  actor: Membership,
  memberGroup: MemberGroup
): SeasonMembership {
  assertMembershipBelongsToMemberGroupChoir(actor, memberGroup);

  return findSeasonMembershipByMembershipId(data, actor.membershipId, memberGroup.choirId, memberGroup.seasonId);
}

function assertMembershipBelongsToMemberGroupChoir(actor: Membership, memberGroup: MemberGroup): void {
  if (actor.choirId !== memberGroup.choirId) {
    throw new Error('Member group belongs to a different choir');
  }
}

function findInvitationCodeById(data: MemberManagementData, invitationCodeId: string): InvitationCode {
  const invitationCode = data.invitationCodes.find((candidate) => candidate.invitationCodeId === invitationCodeId);

  if (!invitationCode) {
    throw new Error('Invitation code not found');
  }

  return invitationCode;
}

function findInvitationCodeByCode(data: MemberManagementData, code: string): InvitationCode {
  const invitationCode = data.invitationCodes.find((candidate) => candidate.code === code);

  if (!invitationCode) {
    throw new Error('Invitation code not found');
  }

  return invitationCode;
}

function findJoinRequest(data: MemberManagementData, joinRequestId: string): JoinRequest {
  const joinRequest = data.joinRequests.find((candidate) => candidate.joinRequestId === joinRequestId);

  if (!joinRequest) {
    throw new Error('Join request not found');
  }

  return joinRequest;
}

function hasSeasonMembership(data: MemberManagementData, userId: string, choirId: string, seasonId: string): boolean {
  return data.memberships.some(
    (membership) =>
      membership.userId === userId &&
      membership.choirId === choirId &&
      data.seasonMemberships.some(
        (seasonMembership) =>
          seasonMembership.membershipId === membership.membershipId && seasonMembership.seasonId === seasonId
      )
  );
}

function hasSeasonMembershipByMembershipId(
  data: MemberManagementData,
  membershipId: string,
  choirId: string,
  seasonId: string
): boolean {
  return data.seasonMemberships.some(
    (seasonMembership) =>
      seasonMembership.membershipId === membershipId &&
      seasonMembership.choirId === choirId &&
      seasonMembership.seasonId === seasonId &&
      seasonMembership.status === 'active'
  );
}

function assertCanReview(data: MemberManagementData, actor: Membership, joinRequest: JoinRequest): void {
  if (actor.choirId !== joinRequest.choirId) {
    throw new Error('Join request belongs to a different choir');
  }

  const currentSeason = findCurrentSeason(data, actor.choirId);

  if (joinRequest.seasonId !== currentSeason.seasonId) {
    throw new Error('Join request is not in the current season');
  }

  const actorSeasonMembership =
    actor.role === 'admin'
      ? undefined
      : data.seasonMemberships.find(
          (seasonMembership) =>
            seasonMembership.membershipId === actor.membershipId &&
            seasonMembership.choirId === actor.choirId &&
            seasonMembership.seasonId === currentSeason.seasonId &&
            seasonMembership.status === 'active'
        )?.seasonMembershipId;
  const effectivePermissionKeys = resolveEffectivePermissionKeys({
    memberRole: actor.role,
    seasonMembershipId: actorSeasonMembership,
    choirId: actor.choirId,
    seasonId: currentSeason.seasonId,
    permissionGroups: data.permissionGroups,
    permissionGroupPermissions: data.permissionGroupPermissions,
    permissionGroupMembers: data.permissionGroupMembers
  });

  if (!effectivePermissionKeys.includes('member_approval')) {
    throw new Error('Member approval permission is required');
  }
}

function assertPending(joinRequest: JoinRequest): void {
  if (joinRequest.status !== 'pending') {
    throw new Error('Join request has already been reviewed');
  }
}

function assertUniqueMemberGroupIds(memberGroupIds: readonly string[]): void {
  if (new Set(memberGroupIds).size !== memberGroupIds.length) {
    throw new Error('Member group IDs must be unique');
  }
}

function assertMemberGroupsBelongToSeason(
  data: MemberManagementData,
  memberGroupIds: readonly string[],
  seasonId: string
): void {
  const validMemberGroupIds = new Set(
    data.memberGroups
      .filter((memberGroup) => memberGroup.seasonId === seasonId)
      .map((memberGroup) => memberGroup.memberGroupId)
  );

  for (const memberGroupId of memberGroupIds) {
    if (!validMemberGroupIds.has(memberGroupId)) {
      throw new Error('Member group must belong to the join request season');
    }
  }
}

function replaceById<T>(items: readonly T[], predicate: (item: T) => boolean, replacement: T): readonly T[] {
  return items.map((item) => (predicate(item) ? replacement : item));
}
